import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames/bind';

import { VendorUsageLocation } from '@SERVICES';
import ROUTES from '@CONSTANTS/ROUTES.constant';

import styles from './VendorUsageIndicator.module.scss';

const cx: CX = classnames.bind(styles);

/**
 * VendorUsageIndicator Component
 * 
 * Displays vendor usage information in a compact, interactive format for the vendors table.
 * 
 * Features:
 * - Shows usage count badge (unused, or used in X docs with Y total usages)
 * - Expandable dropdown with detailed usage locations grouped by document
 * - Click-to-navigate: Users can click on any usage location to go directly to that section/variant
 * - Responsive design that adjusts positioning and size based on screen width
 * - Click-outside-to-close functionality
 * - Visual hierarchy with different colors for section vs variant usage
 * - Smooth animations and hover effects
 * 
 * Navigation behavior:
 * - Section usage: navigates to ROUTES.admin.sections.id.get(sectionId)
 * - Variant usage: navigates to ROUTES.admin.sections.id.get(sectionId, variantId)
 * 
 * Props:
 * @param {VendorUsageLocation[]} usageLocations - Array of usage location objects from the backend
 * @param {string} vendorName - Name of the vendor for display in the header
 */
interface VendorUsageIndicatorProps {
    usageLocations: VendorUsageLocation[];
    vendorName: string;
}

function VendorUsageIndicator({ usageLocations, vendorName }: VendorUsageIndicatorProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const componentRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    // Close when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
                setIsExpanded(false);
            }
        }

        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [isExpanded]);

    const handleLocationClick = (location: VendorUsageLocation) => {
        // Close the dropdown first
        setIsExpanded(false);
        
        // Navigate based on the location type
        if (location.type === 'variant' && location.variant_id) {
            // Navigate to section with variant anchor
            navigate(ROUTES.admin.sections.id.get(location.section_id, location.variant_id));
        } else {
            // Navigate to section
            navigate(ROUTES.admin.sections.id.get(location.section_id));
        }
    };

    if (!usageLocations || usageLocations.length === 0) {
        return (
            <div className={cx('usage-indicator', 'unused')}>
                <span className={cx('usage-badge', 'unused')}>
                    Not used in any document
                </span>
            </div>
        );
    }

    // Group usage by document for better organization
    const usageByDocument = usageLocations.reduce((acc: Record<number, { document_name: string; locations: VendorUsageLocation[] }>, location: VendorUsageLocation) => {
        if (!acc[location.document_id]) {
            acc[location.document_id] = {
                document_name: location.document_name,
                locations: []
            };
        }
        acc[location.document_id].locations.push(location);
        return acc;
    }, {} as Record<number, { document_name: string; locations: VendorUsageLocation[] }>);

    const documentCount = Object.keys(usageByDocument).length;
    const totalUsages = usageLocations.length;

    return (
        <div ref={componentRef} className={cx('usage-indicator', isExpanded ? 'expanded' : '')}>
            <button 
                type="button"
                className={cx('usage-badge', 'used')}
                onClick={() => setIsExpanded(!isExpanded)}
                title={`${isExpanded ? 'Hide' : 'Show'} detailed usage locations with navigation links - This vendor is used in ${documentCount} document${documentCount !== 1 ? 's' : ''} (${totalUsages} total usage${totalUsages !== 1 ? 's' : ''})`}
            >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Used in {documentCount} doc{documentCount !== 1 ? 's' : ''} ({totalUsages})
                <svg 
                    width="10" 
                    height="10" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                    className={cx('expand-icon', { expanded: isExpanded })}
                >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {isExpanded && (
                <div className={cx('usage-details')}>
                    <div className={cx('usage-header')}>
                        <h4>Usage Locations for &quot;{vendorName}&quot;</h4>
                        <p className={cx('usage-help')}>Click on any location below to navigate directly to it</p>
                    </div>
                    <div className={cx('usage-content')}>
                        {Object.entries(usageByDocument).map(([documentId, documentData]) => (
                            <div key={documentId} className={cx('document-group')}>
                                <div className={cx('document-header')}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <span className={cx('document-name')}>{documentData.document_name}</span>
                                    <span className={cx('usage-count')}>({documentData.locations.length} usage{documentData.locations.length !== 1 ? 's' : ''})</span>
                                </div>
                                <div className={cx('locations-list')}>
                                    {documentData.locations.map((location) => (
                                        <div key={`${location.document_id}-${location.chapter_id}-${location.section_id}-${location.variant_id || 'no-variant'}`} className={cx('location-item', location.type)} onClick={() => handleLocationClick(location)} role="button" tabIndex={0}>
                                            <div className={cx('location-type')}>
                                                {location.type === 'section' ? (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M7 8H17M7 12H17M7 16H17M3 4H21V20H3V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                ) : (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M8 7V3M16 7V3M7 11H17M5 21H19C20.1046 21 21 20.1046 21 19V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                                <span className={cx('type-label')}>{location.type}</span>
                                            </div>
                                            <div className={cx('location-path')}>
                                                <span className={cx('chapter-name')}>{location.chapter_name}</span>
                                                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                <span className={cx('section-name')}>{location.section_name}</span>
                                                {location.variant_name && (
                                                    <>
                                                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        <span className={cx('variant-name')}>{location.variant_name}</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className={cx('location-action')}>
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default VendorUsageIndicator; 